import app from 'app';
import Analytics from 'c/WtAnalytics';
import CommonMessageInterface from 'v/notification/CommonMessageInterface.vue';
import Marionette from 'backbone.marionette';

const AutosavePanelViewState = Backbone.Model.extend({
  '?': 'AutosavePanelViewState',

  defaults: {
    event: {},
    progress: false,
    error: false,
    timer: null,
    lastProgress: 0,
    timerDone: null,
    closeCallback: _.noop,
    delay: {
      stayHidden: app.TIME.second(30),
    },
  },
});

export default Marionette.ItemView.extend({
  className: 'wt-autosave-panel',

  onBeforeDestroy: function () {
    this._stopTimer();
  },

  initialize: function (options) {
    this.controller = options.controller;
    this.template = app.compileTemplate.$('#story-autosave-tmpl');
    this.viewState = new AutosavePanelViewState(null, {
      view: this,
    });
    this.listenTo(this.viewState, 'change:event', this._changeEvent);
    this.listenTo(this.viewState, 'change:error', this._changeError);
    this.listenTo(app, 'story:autosave:progress', (progressEvent) => {
      const error = this.viewState.get('error');
      this.viewState.set({
        event: progressEvent,
        progress: progressEvent.inProgress,
        error: _.isDefined(progressEvent.error) ? progressEvent.error : error,
      });
    });

    if (this.model.options.editable) {
      this.listenTo(
        this.model.modelState,
        'change:isDirty',
        this._storyDirtyState_Changed
      );
    }

    if (app.rootStore.getters['user/useAutosave']) {
      this.listenToOnce(app, 'story:saved', () => {
        this.ui.$text[0].textContent = app.culture.story.autosave.done;
      });
    }

    this.viewState.set({
      timerDone: new app.namespace.Util.Timer(() => {
        if (this.isVisible() && $.isElement(this.ui.$text)) {
          this.ui.$text[0].textContent = app.culture.story.autosave.done;
        }
      }, 1 /* not relevant, controlled outside*/),
    });

    this.ErrorComponent = CommonMessageInterface.extend({
      // language=Vue
      template: `
<div class="-moto-manual-notification -auto-save-manual-notification"
    :class="messageType">
    <div class="-info-box">
        <div class="-title">
           <span class="wt2-text-m3 wt2-color-negative">
              {{msg.title}}
           </span>
        </div>
        <div class="-message wt2-text-n" 
             v-html="msg.text">   
        </div>
        <button class="-action-button" 
                 @click="refresh">
          Refresh
        </button>
    </div>
    <slot name="closeItemSlot" 
         :closeItem="closeItem"
     />
</div>
`.normalizeSpace(),

      methods: {
        refresh() {
          location.reload();
        },
      },
    });
  },

  ui: {
    $action: '.js-action[data-action]',
    $saveBtn: '.js-action[data-action="save"]',
    $textBox: '.js-text-box',
    $text: '.js-text',
  },

  events: {
    'click @ui.$action': '_actionClick',
  },

  _actionClick: _.debounce(
    function (e) {
      const action = $(e.currentTarget).data('action');
      switch (action) {
        case 'save':
          app.cursorWorking.show();
          this.ui.$saveBtn.addClass('working');
          this.ui.$saveBtn[0].disabled = true;

          this.deferredSave = new $.Deferred();
          this.deferredSave
            .done(() => {
              if (this.isVisible()) {
                this.ui.$saveBtn.removeClass('working');
              }
            })
            .fail(() => {
              if (this.isVisible()) {
                this.ui.$saveBtn.removeClass('working');
                this.ui.$saveBtn[0].disabled = false;
              }
            })
            .always(() => {
              app.cursorWorking.hide();
              if (this.isVisible()) {
                this.ui.$saveBtn[0].disabled =
                  !this.model.modelState.get('isDirty');
              }
            });

          this.controller.save().then((response) => {
            // report save
            Analytics.trigger('analytics:story:saved:manually', {
              SecondsPassed: Math.round(
                (Date.now() - this.model.modelState.get('timeOfLastSave')) / 1e3
              ),
            });
            this.deferredSave.resolve(response);
          }, this.deferredSave.reject);
          break;

        default:
          app.doctorController.report(`'${action}' action not implemented`);
      }
    },
    2 * app.TIME.MENTAL.λ,
    { leading: true, trailing: false }
  ),

  _storyDirtyState_Changed: function (storyModelState, isDirty) {
    if (
      !this.isVisible() ||
      // will be evaluated upon deferredSave resolving
      (this.deferredSave && this.deferredSave.state() === 'pending')
    ) {
      return;
    }

    // autosave has its own visual representation
    if (app.rootStore.getters['user/useAutosave']) {
      this.ui.$saveBtn.addClass('hide');
      return;
    }

    this.ui.$saveBtn[0].disabled = !isDirty;
  },

  _changeEvent: function (vs, event) {
    let text = '';

    if (this.viewState.get('error')) {
      text = app.culture.story.autosave.failed;
      this.ui.$text[0].textContent = text;
    } else {
      if (event.inProgress) {
        text = app.culture.story.autosave.progress;
        this.ui.$text[0].textContent = text;
        this.viewState.set('lastProgress', Date.now());
      } else {
        this._delayedStatusDone();
      }
    }
  },

  /**
   * Product request: show progress indication not shorter than 1.5 sec.
   * @private
   */
  _delayedStatusDone: function () {
    const passedTime = Date.now() - this.viewState.get('lastProgress');
    const LITTLE_DELAY = app.TIME.second(1.5);

    if (passedTime < LITTLE_DELAY) {
      const delay = LITTLE_DELAY - passedTime;
      this.viewState.get('timerDone').stop().setDelay(delay).start();
    } else {
      this.viewState.get('timerDone').trigger();
    }
  },

  _stopTimer() {
    const timer = this.viewState.get('timer');
    timer && timer.stop();
  },

  _changeError: function (vs, error) {
    this.ui.$text.toggleClass('wt-error', error);
    this._stopTimer();
    const closeCallback = this.viewState.get('closeCallback');
    closeCallback && closeCallback();

    if (error) {
      this._showNotification({
        text: app.culture.story.autosave.popup.failedText,
        title: app.culture.story.autosave.popup.failedTitle,
        type: 'ERROR',
        customComponent: this.ErrorComponent,
        periodic: true,
        timeout: app.TIME.minute(30),
      });
    } else {
      this._showNotification({
        text: app.culture.story.autosave.popup.done,
        type: 'INFO',
        periodic: false,
        timeout: app.TIME.second(10),
      });
    }
  },

  _showNotification: function (options) {
    options = $.extend(
      {
        text: '// TODO: add text',
        type: 'ERROR',
        periodic: false,
        timeout: null,
        customComponent: null,
        title: null,
      },
      options
    );

    app.notificationController.push({
      text: options.text,
      title: options.title,
      type: options.type,
      timeout: options.timeout,
      uniqueGroupName: 'AUTOSAVE_ALERTS',
      customComponent: options.customComponent,
      on: {
        close: (e) => {
          if (e && options.periodic) {
            this.viewState.set(
              'timer',
              new app.namespace.Util.Timer(() => {
                this._changeError(null, true);
              }, this.viewState.get('delay').stayHidden)
            );

            this.viewState.get('timer').start();
          }
        },
      },
      trigger: {
        close: (callback) => {
          this.viewState.set(
            'closeCallback',
            _.isFunction(callback) ? callback : _.noop
          );
        },
      },
    });
  },

  serializeData: function () {
    return {
      _bAutosaveRelevant: app.rootStore.getters['user/useAutosave'],
    };
  },
});
